import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Table, Alert } from "react-bootstrap";
import moment from "moment";
import {
  postOrdenCobro,
  getCreditosDisponiblesOrdenCobro,
} from "../helpers/rutaOrdenesCobro";
import { generarPDFOrdenCobro } from "../helpers/pdfOrdenCobro";
import { getComandasPendientesByCliente } from "../helpers/rutaComandas";
import { generarPDFSaldoCliente } from "../helpers/pdfSaldoCliente";
import MediosDePago from "./MediosDePago";

/* ---------------- helpers ---------------- */
const toISO = (d) => (d ? new Date(d).toISOString() : undefined);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (n) => round2(Number(n || 0));
const $ = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const canonRet = (t) => {
  const s = String(t || "").trim().toLowerCase();
  if (s.startsWith("iib")) return "iibb";
  if (s.includes("ganan")) return "ganancias";
  if (s.includes("iva")) return "iva";
  if (s.includes("suss") || s.includes("s.u.s.s")) return "suss";
  return null;
};

const clamp = (value, min, max) => Math.min(Math.max(num(value), min), max);

const buildRowFromPendiente = (c) => ({
  _id: c._id,
  codcom: c.codcom,
  nroviaje: c.nroviaje,
  fecha: c.fecha,
  factura: c.factura,
  saldo: num(c.saldo ?? c.total ?? 0),
  total: num(c.total ?? 0),
  importeAplicado: num(c.importeAplicado ?? c.saldo ?? c.total ?? 0),
});

export default function ModalOrdenCobro({
  show,
  onHide,
  cliente,
  comandasPreseleccionadas = [],
}) {
  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [nroOrdenCliente, setNroOrdenCliente] = useState("");
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");

  const [medios, setMedios] = useState([]);
  const [rets, setRets] = useState([]);
  const [okMedios, setOkMedios] = useState(false);

  const [creditosDisponibles, setCreditosDisponibles] = useState([]);
  const [saldosFavorAplicados, setSaldosFavorAplicados] = useState([]);
  const [loadingCreditos, setLoadingCreditos] = useState(false);

  /* ---------------- carga inicial ---------------- */
  useEffect(() => {
    if (!show || !cliente?._id) return;

    (async () => {
      const list = await getComandasPendientesByCliente(cliente._id);
      const ordenadas = (list || [])
        .slice()
        .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

      setPendientes(ordenadas);

      const mapSel = new Map();

      comandasPreseleccionadas.forEach((c) => {
        const found = ordenadas.find((x) => x._id === c._id);
        const row = buildRowFromPendiente({
          ...(found || c),
          importeAplicado: found?.saldo ?? c.saldo ?? found?.total ?? c.total ?? 0,
        });
        if (row.saldo > 0.01) mapSel.set(row._id, row);
      });

      setSeleccion(Array.from(mapSel.values()));
    })();
  }, [show, cliente?._id, comandasPreseleccionadas]);

  useEffect(() => {
    if (!show || !cliente?._id) return;

    (async () => {
      try {
        setLoadingCreditos(true);
        const res = await getCreditosDisponiblesOrdenCobro(cliente._id);
        setCreditosDisponibles(res?.ok ? res.creditos || [] : []);
      } catch {
        setCreditosDisponibles([]);
      } finally {
        setLoadingCreditos(false);
      }
    })();
  }, [show, cliente?._id]);

  /* ---------------- totales ---------------- */
  const aplicacionesPayload = useMemo(
    () =>
      seleccion
        .filter((x) => num(x.importeAplicado) > 0.01)
        .map((x) => ({
          comanda: x._id,
          importe: num(x.importeAplicado),
        })),
    [seleccion]
  );

  const totalAplic = useMemo(
    () => round2(aplicacionesPayload.reduce((a, x) => a + num(x.importe), 0)),
    [aplicacionesPayload]
  );

  const totalMedios = useMemo(
    () => round2((medios || []).reduce((a, m) => a + num(m.importe), 0)),
    [medios]
  );

  const totalRets = useMemo(
    () => round2((rets || []).reduce((a, r) => a + num(r.importe), 0)),
    [rets]
  );

  const saldosFavorPayload = useMemo(
    () =>
      (saldosFavorAplicados || [])
        .filter((x) => num(x.importe) > 0.01)
        .map((x) => ({
          ordenCobroOrigen: x._id,
          importe: num(x.importe),
        })),
    [saldosFavorAplicados]
  );

  const totalSaldosFavorAplicados = useMemo(
    () =>
      round2(
        (saldosFavorAplicados || []).reduce((a, x) => a + num(x.importe), 0)
      ),
    [saldosFavorAplicados]
  );

  const totalDisponible = useMemo(
    () => round2(totalMedios + totalRets + totalSaldosFavorAplicados),
    [totalMedios, totalRets, totalSaldosFavorAplicados]
  );

  const diferencia = useMemo(
    () => round2(totalDisponible - totalAplic),
    [totalDisponible, totalAplic]
  );

  const totalSaldoFavorGenerado = useMemo(
    () => (diferencia > 0.01 ? diferencia : 0),
    [diferencia]
  );

  const okDistribucion = diferencia >= -0.01;
  const okMediosGuardado = totalMedios <= 0.01 ? true : okMedios;
  const hasOrigenFondos = totalDisponible > 0.01;
  const puedeGuardar =
    !!nroOrdenCliente &&
    hasOrigenFondos &&
    okDistribucion &&
    okMediosGuardado &&
    (aplicacionesPayload.length > 0 || totalSaldoFavorGenerado > 0.01);

  /* ---------------- selección manual ---------------- */
  const isSelected = (id) => !!seleccion.find((x) => x._id === id);

  const toggleSeleccion = (c) => {
    setSeleccion((prev) => {
      const exists = prev.find((x) => x._id === c._id);
      if (exists) return prev.filter((x) => x._id !== c._id);

      const row = buildRowFromPendiente({
        ...c,
        importeAplicado: c.saldo ?? c.total ?? 0,
      });
      return [...prev, row];
    });
  };

  const updateImporteAplicado = (id, value) => {
    setSeleccion((prev) =>
      prev.map((x) =>
        x._id === id
          ? {
              ...x,
              importeAplicado: clamp(value, 0, num(x.saldo)),
            }
          : x
      )
    );
  };

  const seleccionarTodas = () => {
    const rows = (pendientes || [])
      .filter((p) => num(p.saldo) > 0.01)
      .map((p) =>
        buildRowFromPendiente({
          ...p,
          importeAplicado: p.saldo,
        })
      );

    setSeleccion(rows);
  };

  const deseleccionarTodas = () => setSeleccion([]);

  const aplicarTodoSeleccionado = () => {
    setSeleccion((prev) =>
      prev.map((x) => ({
        ...x,
        importeAplicado: num(x.saldo),
      }))
    );
  };

  const limpiarImportesSeleccionados = () => {
    setSeleccion((prev) =>
      prev.map((x) => ({
        ...x,
        importeAplicado: 0,
      }))
    );
  };

  const aplicarPorFechaSegunDisponible = () => {
    let disponible = num(totalDisponible);

    const ordenadas = [...pendientes]
      .filter((p) => num(p.saldo) > 0.01)
      .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

    const nuevas = [];

    for (const p of ordenadas) {
      if (disponible <= 0.01) break;

      const saldo = num(p.saldo);
      const aplicado = Math.min(saldo, disponible);

      if (aplicado > 0.01) {
        nuevas.push(
          buildRowFromPendiente({
            ...p,
            importeAplicado: aplicado,
          })
        );
        disponible = round2(disponible - aplicado);
      }
    }

    setSeleccion(nuevas);
  };

  /* ---------------- saldo a favor disponible ---------------- */
  const toggleSaldoFavor = (c) => {
    setSaldosFavorAplicados((prev) => {
      const exists = prev.find((x) => x._id === c._id);
      if (exists) return prev.filter((x) => x._id !== c._id);

      return [
        ...prev,
        {
          _id: c._id,
          nroOrdenCliente: c.nroOrdenCliente,
          fecha: c.fecha,
          disponible: num(c.disponible),
          importe: num(c.disponible),
        },
      ];
    });
  };

  const updateImporteSaldoFavor = (id, value) => {
    setSaldosFavorAplicados((prev) =>
      prev.map((x) =>
        x._id === id
          ? {
              ...x,
              importe: clamp(value, 0, num(x.disponible)),
            }
          : x
      )
    );
  };

  const usarTodoSaldoFavor = () => {
    const rows = (creditosDisponibles || [])
      .filter((x) => num(x.disponible) > 0.01)
      .map((x) => ({
        _id: x._id,
        nroOrdenCliente: x.nroOrdenCliente,
        fecha: x.fecha,
        disponible: num(x.disponible),
        importe: num(x.disponible),
      }));

    setSaldosFavorAplicados(rows);
  };

  const limpiarSaldosFavor = () => setSaldosFavorAplicados([]);

  /* ---------------- retenciones ---------------- */
  const addRet = (r) => setRets((prev) => [...prev, r]);
  const removeRet = (i) => setRets((prev) => prev.filter((_, idx) => idx !== i));

  /* ---------------- serialización medios ---------------- */
  const serializeMedio = (m) => {
  const metodoPagoId =
    (m?.metodoPago && typeof m.metodoPago === "string" && m.metodoPago) ||
    (m?.metodoPago &&
      typeof m.metodoPago === "object" &&
      m.metodoPago._id) ||
    null;

  const rawTipo = String(m.tipo || m.medio || "").toLowerCase().trim();

  const tipoLower =
    rawTipo === "icheq" || rawTipo === "icheck"
      ? "echeq"
      : rawTipo === "cheque" || rawTipo === "cheque_tercero"
      ? "cheque_tercero"
      : rawTipo;

  const base = {
    tipo: tipoLower,
    medio: tipoLower,

    importe: round2(m.importe),
    fecha: toISO(m.fecha || fecha),
    fechaValor: toISO(m.fechaValor),

    referencia: m.referencia || undefined,

    numero:
      (tipoLower === "cheque_tercero" && (m.nroCheque || m.numero)) ||
      (tipoLower === "echeq" && (m.idICheq || m.numero)) ||
      m.numero ||
      undefined,

    refTransferencia:
      m.refTransferencia || m.referencia || m.numero || undefined,
  };

  const segunMedio = {
    ...(tipoLower === "efectivo" ? { caja: m.caja || undefined } : {}),

    ...(tipoLower === "transferencia"
      ? { cuentaBancaria: m.cuentaBancaria || undefined }
      : {}),

    ...(tipoLower === "cheque_tercero"
      ? {
          nroCheque: m.nroCheque || m.numero || undefined,
          bancoEmisor: m.bancoEmisor || undefined,
          librador: m.librador || undefined,
          cuitLibrador: m.cuitLibrador || undefined,
          fechaPagoDiferido: toISO(m.fechaPagoDiferido),
        }
      : {}),

    ...(tipoLower === "echeq"
      ? {
          idICheq: m.idICheq || m.numero || undefined,
          bancoEmisor: m.bancoEmisor || undefined,
          emisor: m.emisor || undefined,
          fechaLiquidacion: toISO(m.fechaLiquidacion),
        }
      : {}),
  };

  return {
    ...base,
    ...segunMedio,
    ...(metodoPagoId ? { metodoPago: metodoPagoId } : {}),
  };
};

  /* ---------------- guardar ---------------- */
  const guardar = async () => {
    if (!nroOrdenCliente) {
      alert("Ingresá el número de orden del cliente.");
      return;
    }

    if (!hasOrigenFondos) {
      alert(
        "Debés informar medios, retenciones o saldo a favor aplicado para poder guardar."
      );
      return;
    }

    if (!okMediosGuardado) {
      alert(
        "Revisá los medios de pago (importe > 0 y campos obligatorios)."
      );
      return;
    }

    if (!okDistribucion) {
      alert(
        "Lo aplicado a comandas supera el total disponible de la orden."
      );
      return;
    }

    const mediosCanon = (medios || []).map(serializeMedio);

    const retencionesCanon = (rets || [])
      .filter((r) => num(r.importe) > 0)
      .map((r) => ({
        tipo: canonRet(r.tipo || r.nombre || r.label),
        numero: r.numero || "",
        fecha: toISO(r.fecha || fecha),
        importe: round2(r.importe),
      }));

    if (retencionesCanon.some((r) => !r.tipo)) {
      alert(
        "Hay retenciones sin tipo válido. Usá IIBB / GANANCIAS / IVA / SUSS o borrá la fila."
      );
      return;
    }

    const payload = {
      cliente: cliente._id,
      nroOrdenCliente,
      fecha,
      observaciones,
      aplicaciones: aplicacionesPayload,
      saldosFavorAplicados: saldosFavorPayload,
      medios: mediosCanon,
      retenciones: retencionesCanon,

      totalAplicaciones: totalAplic,
      totalMedios,
      totalRetenciones: totalRets,
      totalSaldosFavorAplicados,
      totalSaldoFavorGenerado,
    };

    console.log("OC payload >>>", JSON.stringify(payload, null, 2));

    const res = await postOrdenCobro(payload);

    if (res?.ok) {
      alert("Orden de cobro guardada correctamente.");
      try {
        generarPDFOrdenCobro(res.ordenCobro);
      } catch (e) {
        console.error("Error generando PDF:", e);
      }
      onHide(true);
    } else {
      console.error("OC error:", res);
      alert(res?.msg || res?.error || "Error al guardar la orden.");
    }
  };

  /* ---------------- exportar PDF ---------------- */
  const exportarSeleccionadasPDF = () => {
    if (!seleccion.length) {
      alert("No hay comandas seleccionadas para exportar.");
      return;
    }

    const items = seleccion.map((c) => {
      const total = num(c.total);
      const saldo = num(c.saldo);
      const aplicado = num(c.importeAplicado);

      return {
        numero: String(c.codcom || ""),
        nroviaje: String(c.nroviaje || ""),
        fecha: c.fecha ? new Date(c.fecha) : null,
        total,
        aplicado,
        saldo: round2(total - aplicado),
      };
    });

    const totalSaldo = items.reduce((s, i) => s + num(i.saldo), 0);
    const clienteNombre =
      cliente?.razonsocial || cliente?.nombre || cliente?.nombreFantasia || "";
    const fechaCorte = fecha ? new Date(fecha) : new Date();

    generarPDFSaldoCliente({
      clienteNombre,
      fechaCorte,
      data: {
        totalSaldoComandas: totalSaldo,
        comandasConSaldo: items,
        ordenesCobroAbiertas: [],
      },
      titulo: "INFORME DE SALDOS PENDIENTES",
    });
  };

  const exportarTodoPDF = () => {
    if (!pendientes.length) {
      alert("No hay comandas pendientes para exportar.");
      return;
    }

    const items = pendientes.map((c) => {
      const total = num(c.total);
      const saldo = num(c.saldo);
      const aplicado = Math.max(total - saldo, 0);

      return {
        numero: String(c.codcom || ""),
        nroviaje: String(c.nroviaje || ""),
        fecha: c.fecha ? new Date(c.fecha) : null,
        total,
        aplicado,
        saldo,
      };
    });

    const totalSaldo = items.reduce((s, i) => s + num(i.saldo), 0);
    const clienteNombre =
      cliente?.razonsocial || cliente?.nombre || cliente?.nombreFantasia || "";
    const fechaCorte = fecha ? new Date(fecha) : new Date();

    generarPDFSaldoCliente({
      clienteNombre,
      fechaCorte,
      data: {
        totalSaldoComandas: totalSaldo,
        comandasConSaldo: items,
        ordenesCobroAbiertas: [],
      },
      titulo: "INFORME DE SALDOS PENDIENTES",
    });
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Nueva Orden de Cobro</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Cliente</Form.Label>
            <Form.Control value={cliente?.razonsocial || ""} readOnly />
          </Col>
          <Col md={4}>
            <Form.Label>N° Orden del Cliente</Form.Label>
            <Form.Control
              value={nroOrdenCliente}
              onChange={(e) => setNroOrdenCliente(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </Col>
        </Row>

        <Row className="align-items-center mb-2">
          <Col className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <b className="me-3">Comandas pendientes</b>

            <div className="d-inline-flex flex-wrap gap-2">
              <Button size="sm" variant="outline-secondary" onClick={seleccionarTodas}>
                Tildar todas
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={deseleccionarTodas}>
                Destildar todas
              </Button>
              <Button size="sm" variant="outline-primary" onClick={aplicarTodoSeleccionado}>
                Aplicar saldo completo
              </Button>
              <Button size="sm" variant="outline-warning" onClick={limpiarImportesSeleccionados}>
                Poner importes en 0
              </Button>
              <Button size="sm" variant="warning" onClick={aplicarPorFechaSegunDisponible}>
                Aplicar por fecha según disponible
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={exportarTodoPDF}
                style={{ minWidth: 150 }}
              >
                Exportar TODO PDF
              </Button>
              <Button
                size="sm"
                variant="outline-success"
                onClick={exportarSeleccionadasPDF}
                disabled={!seleccion.length}
                style={{ minWidth: 190 }}
              >
                Exportar SELECCIONADAS PDF
              </Button>
              {Boolean(seleccion.length) && (
                <span className="ms-2">
                  Seleccionadas: <b>{seleccion.length}</b>
                </span>
              )}
            </div>
          </Col>
        </Row>

        <div
          style={{
            maxHeight: 320,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <Table size="sm" bordered hover className="m-0">
            <thead>
              <tr
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f8f9fa",
                  zIndex: 2,
                }}
              >
                <th style={{ width: 40, textAlign: "center" }}></th>
                <th>Comanda</th>
                <th>Viaje</th>
                <th>Fecha</th>
                <th>Factura</th>
                <th style={{ textAlign: "right" }}>Saldo</th>
                <th style={{ textAlign: "right", width: 150 }}>Aplicar</th>
                <th style={{ textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((c) => {
                const checked = isSelected(c._id);
                const disabled = num(c.saldo) <= 0.01;
                const selectedRow = seleccion.find((x) => x._id === c._id);
                const inputId = `sel-${c._id}`;

                return (
                  <tr key={c._id} style={disabled ? { opacity: 0.5 } : undefined}>
                    <td style={{ width: 40, textAlign: "center" }}>
                      <input
                        id={inputId}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        style={{ marginLeft: 0, position: "static" }}
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggleSeleccion(c)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>

                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        #{String(c.codcom ?? "").padStart(6, "0")}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        {c.nroviaje}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        {c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0">
                        {c.factura || ""}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">
                        ${$(c.saldo ?? 0)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        disabled={!checked}
                        value={checked ? num(selectedRow?.importeAplicado) : ""}
                        onChange={(e) =>
                          updateImporteAplicado(c._id, e.target.value)
                        }
                        onBlur={(e) =>
                          updateImporteAplicado(c._id, e.target.value)
                        }
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0">
                        ${$(c.total ?? 0)}
                      </label>
                    </td>
                  </tr>
                );
              })}

              {!pendientes.length && (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    Sin pendientes para este cliente
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Row className="mt-3">
          <Col md={12}>
            <h6>Saldo a favor disponible</h6>

            <div className="d-flex gap-2 mb-2">
              <Button size="sm" variant="outline-secondary" onClick={usarTodoSaldoFavor}>
                Usar todo
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={limpiarSaldosFavor}>
                Limpiar
              </Button>
            </div>

            {loadingCreditos && (
              <Alert variant="light" className="py-2">
                Cargando saldos a favor disponibles...
              </Alert>
            )}

            {!loadingCreditos && (
              <div
                style={{
                  maxHeight: 180,
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Table size="sm" bordered hover className="m-0">
                  <thead>
                    <tr>
                      <th style={{ width: 40, textAlign: "center" }}></th>
                      <th>OC Origen</th>
                      <th>Fecha</th>
                      <th style={{ textAlign: "right" }}>Disponible</th>
                      <th style={{ textAlign: "right", width: 150 }}>Usar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(creditosDisponibles || []).map((c) => {
                      const checked = !!saldosFavorAplicados.find((x) => x._id === c._id);
                      const row = saldosFavorAplicados.find((x) => x._id === c._id);

                      return (
                        <tr key={c._id}>
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              className="form-check-input m-0 p-0"
                              checked={checked}
                              onChange={() => toggleSaldoFavor(c)}
                            />
                          </td>
                          <td>{c.nroOrdenCliente}</td>
                          <td>
                            {c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            ${$(c.disponible)}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              disabled={!checked}
                              value={checked ? num(row?.importe) : ""}
                              onChange={(e) =>
                                updateImporteSaldoFavor(c._id, e.target.value)
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}

                    {!creditosDisponibles.length && !loadingCreditos && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No hay saldo a favor disponible para este cliente
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <h6>Medios de pago</h6>
            <MediosDePago
              medios={medios}
              setMedios={setMedios}
              onTotalsChange={() => {}}
              onValidityChange={({ valid }) => setOkMedios(valid)}
            />
            {!okMediosGuardado && (
              <div className="mt-2 text-danger">
                Revisá los medios: importe &gt; 0 y, si es EFECTIVO, seleccioná una
                caja; si es TRANSFERENCIA, una cuenta.
              </div>
            )}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <h6>Retenciones</h6>
            <Button size="sm" onClick={() => addRet({ tipo: "iibb", importe: 0, fecha })}>
              + IIBB
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addRet({ tipo: "ganancias", importe: 0, fecha })}
            >
              + Ganancias
            </Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "iva", importe: 0, fecha })}>
              + IVA
            </Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "suss", importe: 0, fecha })}>
              + SUSS
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>N°</th>
                  <th>Fecha</th>
                  <th>Importe</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>{r.tipo}</td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={r.numero || ""}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, numero: e.target.value } : x
                            )
                          )
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={r.fecha || fecha}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, fecha: e.target.value } : x
                            )
                          )
                        }
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.importe}
                        onChange={(e) =>
                          setRets((prev) =>
                            prev.map((x, idx) =>
                              idx === i
                                ? { ...x, importe: Number(e.target.value) }
                                : x
                            )
                          )
                        }
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeRet(i)}
                      >
                        x
                      </Button>
                    </td>
                  </tr>
                ))}
                {!rets.length && (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      Agregá retenciones si corresponde
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}>
            <b>Total aplicado a comandas:</b> ${$(totalAplic)}
          </Col>
          <Col md={4}>
            <b>Σ Medios:</b> ${$(totalMedios)}
          </Col>
          <Col md={4}>
            <b>Σ Retenciones:</b> ${$(totalRets)}
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <b>Σ Saldo a favor usado:</b> ${$(totalSaldosFavorAplicados)}
          </Col>
          <Col md={4}>
            <b>Total disponible:</b> ${$(totalDisponible)}
          </Col>
          <Col md={4}>
            <b>Saldo a favor generado:</b> ${$(totalSaldoFavorGenerado)}
          </Col>
        </Row>

        <Row className="mt-2">
          <Col md={12}>
            <b>Diferencia disponible - aplicado:</b>{" "}
            <span
              style={{
                color: diferencia >= -0.01 ? "green" : "red",
                fontWeight: 700,
              }}
            >
              {$((diferencia))}
            </span>
          </Col>
        </Row>

        {!okDistribucion && (
          <Alert variant="danger" className="mt-3 mb-0">
            Lo aplicado a comandas supera el total disponible de la orden.
          </Alert>
        )}

        {okDistribucion && totalSaldoFavorGenerado > 0.01 && (
          <Alert variant="warning" className="mt-3 mb-0">
            Esta orden va a generar un saldo a favor de <b>${$(totalSaldoFavorGenerado)}</b>.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={guardar} disabled={!puedeGuardar}>
          Guardar y generar PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}